import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://propinvest.co.za';
export const DEFAULT_OG_IMAGE = 'https://propinvest.co.za/og-image.jpg';
export const DEFAULT_OG_IMAGE_ALT = 'PropInvest™ — Exclusive Property Investments South Africa';

/** Truncate a title at a word boundary if it exceeds maxLen characters. */
export function truncateTitle(title: string, maxLen = 65): string {
  if (title.length <= maxLen) return title;
  const truncated = title.slice(0, maxLen);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '\u2026';
}

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleTags?: string[];
}

export default function SEOHead({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  ogType = 'website',
  noindex,
  jsonLd,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleTags,
}: SEOHeadProps) {
  const canonicalUrl = `${SITE_URL}${path}`;

  // Truncate at word boundary to stay within SEO-safe limits
  const safeTitle = title.length > 69
    ? title.slice(0, 69).replace(/\s+\S*$/, '') + '\u2026'
    : title;
  const safeDescription = description.length > 160
    ? description.slice(0, 160).replace(/\s+\S*$/, '') + '\u2026'
    : description;

  if (import.meta.env.DEV && description.length > 160) {
    console.warn(`[SEOHead] Meta description exceeds 160 chars (${description.length}) on ${path}: "${description.slice(0, 50)}..."`);
  }

  return (
    <Helmet>
      <title>{safeTitle}</title>
      <meta name="description" content={safeDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDescription} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:site_name" content="PropInvest&#8482;" />
      <meta property="og:locale" content="en_ZA" />

      {/* Article metadata */}
      {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
      {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
      {articleAuthor && <meta property="article:author" content={articleAuthor} />}
      {articleTags?.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
